// WAVE12-FIX: follows an infinite list to its last page. The API lists seats (and metrics) oldest
// first, 50 a page, so a screen that reads only the first page misses every newer row once a
// company has more: a new seat was missing from Setup › Seats and the team drawer's picker.
// WF-FIX (2026-10-26): it stops at a failed page (it used to ask again forever) and says so:
// `complete` only once every page is in, `failed` with `retry` for the screen's Retry button.
import { useEffect } from "react";

export type EveryPage = {
  /** Every page is in (no next page, nothing failed or loading). */
  complete: boolean;
  /** A page failed to load; nothing more is asked until `retry`. */
  failed: boolean;
  /** Asks again for what failed (the first page or the next one). */
  retry: () => void;
};

export function useEveryPage(q: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  isSuccess?: boolean;
  isError?: boolean;
  isFetchNextPageError?: boolean;
  refetch?: () => Promise<unknown>;
}): EveryPage {
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  const failed = Boolean(q.isError || q.isFetchNextPageError);
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !failed) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, failed]);
  const nextFailed = Boolean(q.isFetchNextPageError);
  const refetch = q.refetch;
  return {
    complete: q.isSuccess !== false && !failed && !hasNextPage && !isFetchingNextPage,
    failed,
    retry: () => {
      if (nextFailed || !refetch) void fetchNextPage();
      else void refetch();
    },
  };
}
